import React, { useState, useEffect, useCallback } from 'react';
import { Container, Button, Box, Typography, CircularProgress, ThemeProvider, Tabs, Tab } from '@mui/material';
import { motion } from 'framer-motion';
import RefreshIcon from '@mui/icons-material/Refresh';
import QuoteCard from './components/QuoteCard';
import FavoriteQuotes from './components/FavoriteQuotes';
import AiQuotePanel from './components/AiQuotePanel';
import AnimatedBackground from './components/AnimatedBackground';
import theme from './theme';
import './styles.css';

// Définir l'URL de base de l'API en fonction de l'environnement
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? process.env.REACT_APP_API_URL 
  : '';

function App() {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem('favoriteQuotes');
    return saved ? JSON.parse(saved) : [];
  });
  const [activeTab, setActiveTab] = useState(0);

  const fetchQuote = useCallback(async () => {
    setLoading(true);
    setError(null);
    const apiUrl = `${API_BASE_URL}/api/quotes/random`;
    
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Réponse non-OK:', errorText);
        throw new Error(`Erreur réseau: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      setQuote(data);
    } catch (error) {
      console.error('Erreur détaillée:', error);
      setError(`Erreur lors de la récupération de la citation: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  useEffect(() => {
    localStorage.setItem('favoriteQuotes', JSON.stringify(favorites));
  }, [favorites]);

  const handleToggleFavorite = (quote) => {
    setFavorites(prevFavorites => {
      const quoteExists = prevFavorites.some(fav => fav.text === quote.text);
      if (quoteExists) {
        return prevFavorites.filter(fav => fav.text !== quote.text);
      } else {
        return [...prevFavorites, quote];
      }
    });
  };

  const isQuoteFavorite = (candidateQuote) => {
    if (!candidateQuote) return false;
    return favorites.some(fav => fav.text === candidateQuote.text);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <AnimatedBackground />
        <Container maxWidth="md" sx={{ py: 4 }}>
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Typography 
              variant="h2" 
              component="h1"
              align="center"
              sx={{
                mb: 4,
                fontWeight: 700,
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Citations d'Anime
            </Typography>
          </motion.div>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
            <Tabs value={activeTab} onChange={handleTabChange} centered>
              <Tab label="Citations Aléatoires" />
              <Tab label="Générateur IA" />
              <Tab label={`Favoris (${favorites.length})`} />
            </Tabs>
          </Box>

          {activeTab === 0 ? (
            <>
              {loading && !quote ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress size={40} />
                </Box>
              ) : error ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Typography color="error" align="center" sx={{ my: 4 }}>
                    {error}
                  </Typography>
                </motion.div>
              ) : (
                quote && (
                  <>
                    <QuoteCard 
                      quote={quote} 
                      onToggleFavorite={handleToggleFavorite}
                      isFavorite={isQuoteFavorite(quote.quote)}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={fetchQuote}
                          disabled={loading}
                          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                          sx={{
                            borderRadius: '50px',
                            px: 4,
                            py: 1.5,
                            textTransform: 'none',
                            fontSize: '1.1rem',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            '&:hover': {
                              boxShadow: '0 6px 8px rgba(0,0,0,0.2)',
                            }
                          }}
                        >
                          {loading ? 'Chargement...' : 'Nouvelle Citation'}
                        </Button>
                      </motion.div>
                    </Box>
                  </>
                )
              )}
            </>
          ) : activeTab === 1 ? (
            <AiQuotePanel
              apiBaseUrl={API_BASE_URL}
              onToggleFavorite={handleToggleFavorite}
              isQuoteFavorite={isQuoteFavorite}
            />
          ) : (
            <FavoriteQuotes 
              favorites={favorites} 
              onRemoveFavorite={(quote) => handleToggleFavorite(quote)} 
            />
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;
